import { useCallback } from "react";
import { useSDK } from "@contentful/react-apps-toolkit";
import { useTranslationContext } from "../context";
import { Formality, GlossaryId, Locale } from "../lib";
import { areLocalesInGlossary, mapLocale } from "../utils";
import { usePostDeepl } from "./";
import { TranslationResponse } from "./types";

interface DeeplTranslateRequestBody {
    text: string[];
    source_lang: Locale;
    target_lang: Locale;
    tag_handling?: "html";
    context?: string;
    formality: Formality;
    glossary_id?: GlossaryId;
}

export const useDeeplTranslate = () => {
    const sdk = useSDK();
    const { state } = useTranslationContext();
    const { postDeepl } = usePostDeepl();

    const { deeplSourceLocales, deeplTargetLocales } = state;
    const deeplTranslate = useCallback(
        async (
            text: string[],
            sourceLang: Locale,
            targetLang: Locale,
            options: {
                formality: Formality;
                isHtml: boolean;
            }
        ): Promise<string[]> => {
            try {
                if (text.length === 0) {
                    return [];
                }

                const mappedSourceLocale = mapLocale(sourceLang, deeplSourceLocales);
                const mappedTargetLocale = mapLocale(targetLang, deeplTargetLocales);

                if (!mappedSourceLocale) {
                    sdk.notifier.error(`Locale ${sourceLang} unsupported SourceLang.`);
                    return [];
                }

                if (!mappedTargetLocale) {
                    sdk.notifier.error(`Locale ${targetLang} unsupported TargetLang.`);
                    return [];
                }

                const requestBody: DeeplTranslateRequestBody = {
                    text,
                    source_lang: mappedSourceLocale,
                    target_lang: mappedTargetLocale,
                    tag_handling: options.isHtml ? "html" : undefined,
                    context: text.length > 1 ? JSON.stringify(text) : undefined,
                    formality: options.formality,
                    glossary_id: areLocalesInGlossary(
                        state.glossary,
                        mappedSourceLocale,
                        mappedTargetLocale
                    )
                        ? state.glossary?.id
                        : undefined,
                };

                const response: TranslationResponse = await postDeepl(
                    "translate",
                    JSON.stringify(requestBody),
                    "v2"
                );

                if (response && response.translations) {
                    return response.translations.map(translation => translation.text);
                } else {
                    return [];
                }
            } catch (error) {
                if (error instanceof Error) {
                    sdk.notifier.error(error.message);
                } else {
                    sdk.notifier.error("Unknown error occurred");
                }

                return [];
            }
        },
        [deeplSourceLocales, deeplTargetLocales, postDeepl, sdk.notifier, state.glossary]
    );

    return {
        deeplTranslate,
    };
};
